import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import useSWR from "swr";
import useLocalStorage from "@catchy/app-core/src/useLocalStorage";
import { favouriteDetailsKey, updateFavouriteStopDetails } from "@catchy/app-core/src/favouriteStopDetails";
import BackLink from "./BackLink";
import NotFoundContent from "./NotFoundContent";
const contentByPath = {
  stop: {
    title: "This stop is not available",
    description: "This stop doesn't exist or has been temporarily removed.",
    actionLabel: "Remove from favourites"
  },
  trip: {
    title: "Unable to get trip information.",
    description: "This can be caused by Catchy using outdated or incomplete data.",
    actionLabel: "Retry"
  },
  other: {
    title: "404",
    description: "Page not found"
  }
};
export default function NotFoundPage() {
  const router = useRouter();
  const [errorPath, setErrorPath] = useState(null);
  const [stopId, setStopId] = useState(null);
  const [favouriteRemoved, setFavouriteRemoved] = useState(false);
  const [favourites, setFavourites] = useLocalStorage("favourites", []);
  const [, setDetails] = useLocalStorage(favouriteDetailsKey, {});
  const {
    data: stopAlerts
  } = useSWR(stopId ? `/api/servicealerts/stop-id/${encodeURIComponent(stopId)}` : null, async url => {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Stop alerts unavailable");
    const alerts = await response.json();
    return Array.isArray(alerts) ? alerts : [];
  }, {
    revalidateOnFocus: false,
    shouldRetryOnError: false
  });
  useEffect(() => {
    const path = router.asPath || window.location.pathname;
    setErrorPath(path.split("/")[1] || null);
    const match = path.split(/[?#]/)[0].match(/^\/stop\/([^/]+)\/?$/);
    try {
      setStopId(match ? decodeURIComponent(match[1]) : null);
    } catch {
      setStopId(null);
    }
    setFavouriteRemoved(false);
  }, [router.asPath]);
  const removeFavourite = async () => {
    await setFavourites(current => current.filter(id => id !== stopId));
    await setDetails(current_0 => updateFavouriteStopDetails(current_0, stopId, null, false));
    setFavouriteRemoved(true);
  };
  const content = Object.hasOwn(contentByPath, errorPath) ? contentByPath[errorPath] : contentByPath.other;
  const pageTitle = `${content.title} - Catchy`;
  const action = errorPath === "trip" ? () => router.reload() : errorPath === "stop" && stopId && favourites.includes(stopId) ? removeFavourite : undefined;
  return <>
      <Head>
        <title key="title">{pageTitle}</title>
        <meta name="twitter:text:title" content={pageTitle} key="twitterTitle" />
        <meta property="og:title" content={pageTitle} key="ogTitle" />
        <meta name="robots" content="noindex" />
      </Head>
      <NotFoundContent {...content} pageTitle={pageTitle} backControl={<BackLink />} action={action} favouriteRemoved={favouriteRemoved} stopAlerts={errorPath === "stop" ? stopAlerts : []} />
    </>;
}